import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusPill, EnvBadge } from "@/components/StatusPill";
import { useI18n } from "@/i18n/I18nContext";
import { ServerCog, Loader2, AlertTriangle, ChevronRight, RefreshCw, RotateCcw, XCircle, PlayCircle, Save, PackageCheck, GitBranch, RefreshCcw, WandSparkles, Check, X } from "lucide-react";
import { toast } from "sonner";

const KINDS = ["ALL", "COMMAND", "MISSION", "WORKFLOW", "TASK"];
const STATUSES = ["ALL", "BACKLOG", "READY", "QUEUED", "RUNNING", "PAUSED", "WAITING", "REVIEW", "APPROVAL_REQUIRED", "RETRYING", "BLOCKED", "FAILED", "COMPLETED", "CANCELLED"];
const KIND_COLOR = { COMMAND: "#60A5FA", MISSION: "#38BDF8", WORKFLOW: "#A78BFA", TASK: "#94A3B8" };

function dur(a, b) {
  if (!a) return "—";
  const end = b ? new Date(b) : new Date();
  const s = Math.max(0, Math.round((end - new Date(a)) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

export default function JobCenter() {
  const [jobs, setJobs] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [kind, setKind] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [syncing, setSyncing] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const navigate = useNavigate();
  const { t, lang } = useI18n();

  const load = useCallback(() => {
    setJobs(null);
    setLoadError(false);
    api.jobs(kind === "ALL" ? undefined : kind, status).then((jobRows) => {
      setJobs(Array.isArray(jobRows) ? jobRows : []);
      setLoadError(false);
    }).catch(() => { setLoadError(true); setJobs([]); });
    api.automationSuggestions("OPEN").then((suggestionRows) => {
      setSuggestions(Array.isArray(suggestionRows) ? suggestionRows : []);
    }).catch(() => { setSuggestions([]); });
  }, [kind, status]);

  useEffect(() => { load(); }, [load]);

  const drill = (j) => {
    const r = j.ref || {};
    if (r.type === "workflow" && r.run_id) navigate(`/workflows/runs/${r.run_id}`);
    else if (r.mission_id) navigate(`/missions/${r.mission_id}`);
    else if (j.kind === "TASK" && j.project_code) navigate(`/projects/${j.project_code}?tab=tasks`);
  };

  const CANCELABLE = ["QUEUED", "RUNNING", "WAITING", "RETRYING"];
  const RETRYABLE = ["FAILED", "CANCELLED"];

  const act = async (e, j, action) => {
    e.stopPropagation();
    try {
      if (action === "cancel") {
        await api.cancelJob(j.kind, j.id);
        toast.success(t("jobs.cancelled"));
      } else {
        const r = await api.retryJob(j.kind, j.id);
        toast.success(t("jobs.retryLaunched"));
        if (r.run_id) navigate(`/workflows/runs/${r.run_id}`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.actionFailed"));
    }
  };

  const resumeTask = async (e, j) => {
    e.stopPropagation();
    try {
      await api.resumeTask(j.id);
      toast.success(lang === "id"
        ? "Task disiapkan READY dari checkpoint. Eksekusi belum dimulai otomatis."
        : "Task staged READY from checkpoint. Execution was not started automatically.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.actionFailed"));
    }
  };

  const prepareHandoff = async (e, j) => {
    e.stopPropagation();
    try {
      await api.prepareTaskHandoff(j.id);
      toast.success(lang === "id"
        ? "Execution handoff disiapkan. Belum ada tool eksternal yang dijalankan."
        : "Execution handoff prepared. No external tool was started.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.actionFailed"));
    }
  };

  const verifyHandoff = async (e, j) => {
    e.stopPropagation();
    try {
      const r = await api.verifyTaskHandoff(j.id);
      const state = r.trace_verification || "ERROR";
      if (state === "VERIFIED") toast.success("GitHub trace VERIFIED.");
      else if (state === "MISMATCH") toast.error("GitHub trace MISMATCH — Founder review required.");
      else if (state === "NOT_CONFIGURED") toast.warning("GitHub read connector belum dikonfigurasi.");
      else toast.info(`GitHub trace: ${state}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.actionFailed"));
    }
  };

  const resolveSuggestion = async (e, suggestion, action) => {
    e.stopPropagation();
    try {
      await api.decideAutomationSuggestion(suggestion.id, action);
      toast.success(action === "ACKNOWLEDGE"
        ? (lang === "id" ? "Suggestion diakui." : "Suggestion acknowledged.")
        : (lang === "id" ? "Suggestion ditutup." : "Suggestion dismissed."));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.actionFailed"));
    }
  };

  const syncGithub = async () => {
    setSyncing(true);
    try {
      const r = await api.syncExecutionGithub({});
      if (!r.connector_configured) {
        toast.warning(lang === "id"
          ? "GitHub read connector belum dikonfigurasi."
          : "GitHub read connector is not configured.");
      } else if (r.error_count > 0) {
        toast.error(`GitHub sync selesai dengan ${r.error_count} error.`);
      } else {
        toast.success(lang === "id"
          ? `GitHub sync selesai · ${r.synced_count} handoff · ${r.changed_count} berubah · ${r.attention_count} butuh perhatian.`
          : `GitHub sync complete · ${r.synced_count} handoffs · ${r.changed_count} changed · ${r.attention_count} need attention.`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.actionFailed"));
    } finally {
      setSyncing(false);
    }
  };

  const attention = jobs ? jobs.filter((j) => j.founder_attention).length : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="mono-label">FOUNDER EXECUTION</p>
          <h1 className="font-display text-3xl font-extrabold text-white mt-1 flex items-center gap-2">
            <ServerCog className="text-[#2563EB]" /> Eksekusi
          </h1>
          <p className="text-sm text-[#94A3B8] mt-1">
            {lang === "id"
              ? "Catatan pekerjaan, handoff, pemeriksaan bukti, checkpoint, dan hambatan."
              : "Unified runtime for commands, missions, workflows, and tasks — including checkpoint/resume, blockers, retry, and cancel."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={syncGithub} disabled={syncing}
            className="btn-ghost h-10 px-4 text-sm flex items-center gap-2 disabled:opacity-50"
            data-testid="jobs-sync-github">
            <RefreshCcw size={15} className={syncing ? "animate-spin" : ""} />
            {lang === "id" ? "Sync GitHub" : "Sync GitHub"}
          </button>
          <button onClick={load} className="btn-ghost h-10 px-4 text-sm flex items-center gap-2" data-testid="jobs-refresh">
            <RefreshCw size={15} /> {t("jobs.refresh")}
          </button>
        </div>
      </div>

      <p className="text-xs text-[#FBBF24]" data-testid="execution-truth-notice">Status tugas MASANDI tidak otomatis berarti pekerjaan eksternal atau deployment selesai. GitHub Sync hanya memeriksa bukti GitHub dari handoff yang terdaftar.</p>
      {attention > 0 && (
        <div className="m-card p-3 flex items-center gap-2 text-sm text-[#FB7185]" style={{ borderColor: "#BE123C" }}>
          <AlertTriangle size={15} /> {t("jobs.attention", { n: attention })}
        </div>
      )}

      {suggestions.length > 0 && (
        <section className="m-card p-4 space-y-3" data-testid="automation-suggestions">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-display text-sm font-bold text-white flex items-center gap-2">
              <WandSparkles size={15} className="text-[#A78BFA]" /> AUTOMATION NEXT ACTIONS
            </h2>
            <span className="m-pill text-[10px]">{suggestions.length} OPEN</span>
          </div>
          <div className="space-y-2">
            {suggestions.slice(0, 5).map((s) => (
              <div key={s.id} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3"
                data-testid={`automation-suggestion-${s.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="mono-label">{s.project_code || "COMPANY"}</span>
                      <span className="text-white text-sm font-medium">{s.title}</span>
                    </div>
                    {s.detail && <p className="text-xs text-[#64748B] mt-1">{s.detail}</p>}
                    {s.next_action && <p className="text-xs text-[#CBD5E1] mt-2"><span className="text-[#94A3B8]">Next:</span> {s.next_action}</p>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={(e) => resolveSuggestion(e, s, "ACKNOWLEDGE")}
                      className="btn-ghost h-8 px-2 text-[11px] flex items-center gap-1">
                      <Check size={12} /> {lang === "id" ? "Akui" : "Acknowledge"}
                    </button>
                    <button onClick={(e) => resolveSuggestion(e, s, "DISMISS")}
                      className="btn-ghost h-8 px-2 text-[11px] flex items-center gap-1">
                      <X size={12} /> {lang === "id" ? "Tutup" : "Dismiss"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="flex flex-wrap gap-3">
        <div className="flex gap-1 bg-[#0F1626] border border-[#1E293B] rounded-xl p-1">
          {KINDS.map((k) => (
            <button key={k} onClick={() => setKind(k)}
              className={`px-3 py-1.5 rounded-lg text-xs ${kind === k ? "bg-[#2563EB] text-white" : "text-[#94A3B8]"}`}
              data-testid={`jobs-kind-${k}`}>{k}</button>
          ))}
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="input-dark h-9 px-3 text-xs" data-testid="jobs-status-filter">
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loadError ? (
        <div className="m-card p-8 text-center" role="alert" data-testid="execution-api-error">
          <p className="text-white font-semibold">Data eksekusi belum bisa dimuat</p>
          <p className="text-[#94A3B8] text-sm mt-2">Periksa koneksi atau sesi. Tidak menampilkan daftar kosong saat API gagal.</p>
          <button onClick={load} className="btn-primary h-9 px-4 mt-4">Coba lagi</button>
        </div>
      ) : !jobs ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-[#2563EB]" /></div>
      ) : jobs.length === 0 ? (
        <div className="m-card p-10 text-center text-[#64748B] text-sm">{t("jobs.empty")}</div>
      ) : (
        <div className="space-y-2">
          {jobs.map((j) => (
            <div key={`${j.kind}-${j.id}`} onClick={() => drill(j)}
              className="m-card m-card-hover p-4 cursor-pointer" data-testid={`job-row-${j.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="m-pill" style={{ color: KIND_COLOR[j.kind], borderColor: "#1E293B", background: "#0B0F19" }}>{j.kind}</span>
                  <span className="text-white text-sm font-medium truncate">{j.title || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  {j.founder_attention && <AlertTriangle size={14} className="text-[#FB7185]" title={t("jobs.attentionTitle")} />}
                  <EnvBadge environment={j.environment} archived={j.archived} />
                  <StatusPill status={j.status} />
                  {CANCELABLE.includes(j.status) && ["COMMAND", "MISSION", "WORKFLOW"].includes(j.kind) && (
                    <button onClick={(e) => act(e, j, "cancel")} className="btn-ghost h-7 px-2 text-[11px] flex items-center gap-1"
                      style={{ color: "#FCA5A5" }} data-testid={`job-cancel-${j.id}`}>
                      <XCircle size={12} /> {t("jobs.cancel")}
                    </button>
                  )}
                  {RETRYABLE.includes(j.status) && ["COMMAND", "MISSION", "WORKFLOW"].includes(j.kind) && (
                    <button onClick={(e) => act(e, j, "retry")} className="btn-ghost h-7 px-2 text-[11px] flex items-center gap-1"
                      data-testid={`job-retry-${j.id}`}>
                      <RotateCcw size={12} /> Retry
                    </button>
                  )}
                  {j.kind === "TASK" && j.checkpoint && ["PAUSED", "BLOCKED", "FAILED"].includes(j.status) && (
                    <button onClick={(e) => resumeTask(e, j)} className="btn-ghost h-7 px-2 text-[11px] flex items-center gap-1"
                      data-testid={`task-resume-${j.id}`}>
                      <PlayCircle size={12} /> {lang === "id" ? "Siapkan Resume" : "Stage Resume"}
                    </button>
                  )}
                  {j.kind === "TASK" && j.execution_tool && !j.execution_handoff && (
                    <button onClick={(e) => prepareHandoff(e, j)} className="btn-ghost h-7 px-2 text-[11px] flex items-center gap-1"
                      data-testid={`task-handoff-${j.id}`}>
                      <PackageCheck size={12} /> {lang === "id" ? "Siapkan Handoff" : "Prepare Handoff"}
                    </button>
                  )}
                  {j.kind === "TASK" && j.execution_handoff?.target_repo && (
                    <button onClick={(e) => verifyHandoff(e, j)} className="btn-ghost h-7 px-2 text-[11px] flex items-center gap-1"
                      data-testid={`task-verify-trace-${j.id}`}>
                      <GitBranch size={12} /> {lang === "id" ? "Verifikasi GitHub" : "Verify GitHub"}
                    </button>
                  )}
                  <ChevronRight size={15} className="text-[#64748B]" />
                </div>
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] font-mono text-[#64748B]">
                <span>{j.project_code || "—"}</span>
                {j.agent && <span>agent {j.agent}</span>}
                {j.workflow_run_id && <span>run {j.workflow_run_id.slice(0, 6)}</span>}
                {j.current_step && <span>step: {String(j.current_step).replace(/_/g, " ")}</span>}
                <span>{t("jobs.retries", { n: j.retry_count })}</span>
                {j.model && <span>{j.provider}/{j.model}</span>}
                {j.tokens > 0 && <span>{j.tokens} tok</span>}
                <span>${j.cost}</span>
                <span>dur {dur(j.started_at, j.ended_at)}</span>
              </div>

              {j.kind === "TASK" && j.execution_handoff?.handoff_mode === "MANUAL_EXTERNAL" && (
                <p className="mt-2 text-xs text-[#FBBF24]" data-testid={`manual-handoff-${j.id}`}>
                  {j.execution_handoff.trace_verification === "VERIFIED"
                    ? "Bukti GitHub telah diverifikasi. Hasil bisnis dan deployment tetap memerlukan verifikasi terpisah."
                    : "Handoff manual: MASANDI belum memanggil eksekutor eksternal secara otomatis."}
                </p>
              )}
              {j.kind === "TASK" && !j.execution_handoff && j.routing_plan?.dispatch_behavior === "TRACK_ONLY" && (
                <p className="mt-2 text-xs text-[#FBBF24]" data-testid={`tracked-only-${j.id}`}>
                  Baru dicatat; belum ada eksekusi eksternal yang terkonfirmasi.
                </p>
              )}

              {j.routing_plan && (
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] font-mono text-[#64748B]"
                  data-testid={`job-routing-${j.id}`}>
                  <span>ROUTE {j.routing_plan.operating_module}</span>
                  <span>{j.routing_plan.playbook || "NO_PLAYBOOK"}</span>
                  <span>{j.routing_plan.work_type}</span>
                  <span>OWNER {j.routing_plan.functional_owner}</span>
                  <span>TOOL {j.routing_plan.selected_execution_tool}</span>
                  {j.routing_plan.approval_required && <span className="text-[#FCA5A5]">APPROVAL REQUIRED</span>}
                </div>
              )}

              {j.kind === "TASK" && (
                <div className="mt-2 space-y-1 text-[11px] text-[#64748B]">
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {j.execution_tool && <span>tool <span className="text-[#94A3B8]">{j.execution_tool}</span></span>}
                    {j.operating_module && <span>module <span className="text-[#94A3B8]">{j.operating_module}</span></span>}
                    {j.playbook && <span>playbook <span className="text-[#94A3B8]">{j.playbook}</span></span>}
                    {j.qa_state && <span>QA <span className="text-[#94A3B8]">{j.qa_state}</span></span>}
                    {j.branch && <span>git <span className="text-[#94A3B8]">{j.branch}</span></span>}
                    {j.pull_request && <span>PR <span className="text-[#94A3B8]">{j.pull_request}</span></span>}
                  </div>
                  {j.blocker && <p className="text-[#FCA5A5]">BLOCKER · {j.blocker}</p>}
                  {j.next_action && <p><span className="text-[#94A3B8]">Next:</span> {j.next_action}</p>}
                  {j.checkpoint && (
                    <div className="flex items-start gap-1.5 text-[#A5B4FC]" data-testid={`task-checkpoint-${j.id}`}>
                      <Save size={12} className="mt-0.5 shrink-0" />
                      <span>
                        CHECKPOINT · {j.checkpoint.cause}
                        {j.checkpoint.test_state ? ` · test ${j.checkpoint.test_state}` : ""}
                        {j.checkpoint.resume_point ? ` · resume: ${j.checkpoint.resume_point}` : ""}
                      </span>
                    </div>
                  )}
                  {j.execution_handoff && (
                    <div className="rounded-lg border border-[#0E7490] bg-[#083344]/30 p-2.5 text-[10px]"
                      data-testid={`task-handoff-summary-${j.id}`}>
                      <p className="font-mono text-[#67E8F9] flex items-center gap-1.5">
                        <PackageCheck size={12} /> HANDOFF · {j.execution_handoff.status}
                      </p>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-[#94A3B8]">
                        <span>MODE {j.execution_handoff.handoff_mode}</span>
                        {j.execution_handoff.target_repo && <span>REPO {j.execution_handoff.target_repo}</span>}
                        {j.execution_handoff.suggested_branch && <span>SUGGESTED {j.execution_handoff.suggested_branch}</span>}
                        {j.execution_handoff.actual_branch && <span>ACTUAL {j.execution_handoff.actual_branch}</span>}
                        {j.execution_handoff.actual_commit && <span>COMMIT {String(j.execution_handoff.actual_commit).slice(0, 10)}</span>}
                        {j.execution_handoff.actual_pull_request && <span>PR {j.execution_handoff.actual_pull_request}</span>}
                        {j.execution_handoff.test_state && <span>TEST {j.execution_handoff.test_state}</span>}
                      </div>
                      <p className={`mt-1 font-mono ${
                        j.execution_handoff.trace_verification === "VERIFIED" ? "text-[#34D399]" :
                        j.execution_handoff.trace_verification === "MISMATCH" ? "text-[#FB7185]" :
                        "text-[#64748B]"
                      }`}>
                        TRACE {j.execution_handoff.trace_verification || "UNVERIFIED_REPORTED"}
                        {j.execution_handoff.verified_at ? ` · ${new Date(j.execution_handoff.verified_at).toLocaleString()}` : ""}
                      </p>
                      {j.execution_handoff.verified_trace?.reason && (
                        <p className="mt-1 text-[#94A3B8]">{j.execution_handoff.verified_trace.reason}</p>
                      )}
                      {j.execution_handoff.last_sync_at && (
                        <p className="mt-1 text-[#64748B] font-mono">
                          SYNC {new Date(j.execution_handoff.last_sync_at).toLocaleString()}
                          {j.execution_handoff.sync_change_keys?.length ? ` · changed ${j.execution_handoff.sync_change_keys.join(", ")}` : " · no material field change"}
                        </p>
                      )}
                      {j.execution_handoff.sync_attention_reasons?.length > 0 && (
                        <p className="mt-1 text-[#FB7185] font-mono">
                          ATTENTION · {j.execution_handoff.sync_attention_reasons.join(" · ")}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
